import { t } from 'i18next';
import { isValidPhoneNumber } from 'libphonenumber-js';
import * as yup from 'yup';

import {
  LENGTH_XXS,
  LENGTH_XS,
  LENGTH_S,
  LENGTH_M,
  LENGTH_L,
  LENGTH_XL,
  LENGTH_1024,
  LENGTH_XXXL,
} from '../constants';
import { slug } from '../patterns';

export const integrationRulesFacebook = {
  domains: { required: false, maxLength: LENGTH_XL },
  accessToken: { required: true, maxLength: LENGTH_1024 },
  verifyToken: { required: true, maxLength: LENGTH_1024 },
  appSecret: { required: true, maxLength: LENGTH_1024 },
  pageId: { required: true, maxLength: LENGTH_XS },
  welcomeTriggerMessage: { required: false, maxLength: LENGTH_S },
  greetingText: { required: false, maxLength: LENGTH_M },
};

export const integrationRulesIntercom = {
  accessToken: { required: true, maxLength: LENGTH_XL },
  appId: { required: true, maxLength: LENGTH_S },
  welcomeTriggerMessage: { required: false, maxLength: LENGTH_S },
};

export const integrationRulesViber = {
  name: { required: true, maxLength: LENGTH_S },
  accessToken: { required: true, maxLength: LENGTH_XL },
  avatar: { required: true },
  welcomeTriggerMessage: { required: false, maxLength: LENGTH_S },
};

/**
 * MSTAT Viber Business field limits. Mirrors the orchestrator
 * `mstatViberIntegrationConfig` schema: `webhook_token` 12–128, `api_token`
 * 1–1024, `username` 1–128, `password` 1–256. Auth is `api_token` XOR
 * (`username` + `password`) — enforced in the connect form on submit.
 */
export const integrationRulesMstatViber = {
  webhookToken: { required: true, minLength: 12, maxLength: LENGTH_M },
  apiToken: { required: false, maxLength: LENGTH_1024 },
  username: { required: false, maxLength: LENGTH_M },
  password: { required: false, maxLength: LENGTH_L },
};

const forbiddenPrefixes = ['user', 'global', 'tags', 'channels'];

const saveAsRefRegex = /^\w[\w-]*(?:\.[\w-]+)*$/i;

const formFieldVariable = (schema: yup.StringSchema) =>
  schema
    .min(2, t('validation.min_value', { 0: 2 }))
    .max(256, t('validation.max_value', { 0: 256 }))
    .matches(saveAsRefRegex, t('validation.invalid_path_format'))
    .test(
      'forbidden-prefix',
      t('validation.forbidden_prefix', { 0: forbiddenPrefixes.join(', ') }),
      (value) => {
        if (!value) {
          return true;
        }
        return !forbiddenPrefixes.some(
          (prefix) => value === prefix || value.startsWith(`${prefix}.`)
        );
      }
    );

const webFormFields = yup.object().shape({
  field: yup.string().required(() => t('validation.required')),
  required: yup.boolean().required(() => t('validation.required')),
  languages: yup
    .array()
    .of(yup.object().shape({ label: yup.string().required(), value: yup.string().required() }))
    .when('field', {
      is: 'language',
      then: (schema) => schema.min(2, t('validation.at_least_two_languages')).required(),
      otherwise: (schema) => schema.strip(),
    }),
  field_label: yup
    .string()
    .max(LENGTH_XS, () => t('validation.less_than', { 0: LENGTH_XS }))
    .when('field', {
      is: 'custom',
      then: (schema) => schema.required(t('validation.required')),
      otherwise: (schema) => schema.strip(),
    }),
  variable: yup
    .string()
    .when('field', {
      is: 'custom',
      then: (schema) => formFieldVariable(schema).required(t('validation.required')),
      otherwise: (schema) => schema.strip(),
    }),
  variable_type: yup
    .string()
    .when('field', {
      is: 'custom',
      then: (schema) => schema.required(t('validation.required')),
      otherwise: (schema) => schema.strip(),
    }),
});

const webVisitorInformation = yup
  .object()
  .shape({
    fields: yup
      .array()
      .when('collect_data_checkbox', {
        is: true,
        then: (schema) => schema.of(webFormFields),
        otherwise: (schema) => schema.strip(),
      }),
    headline: yup
      .string()
      .when('collect_data_checkbox', {
        is: true,
        then: (schema) => schema.max(LENGTH_XXXL, t('validation.less_than', { 0: LENGTH_XXXL })),
        otherwise: (schema) => schema.strip(),
      }),
    disclaimer: yup
      .string()
      .when('disclaimer_checkbox', {
        is: true,
        then: (schema) => schema.max(LENGTH_XXXL, t('validation.less_than', { 0: LENGTH_XXXL })),
        otherwise: (schema) => schema.strip(),
      }),
    disclaimer_checkbox: yup.boolean(),
    collect_data_checkbox: yup.boolean(),
    collect_location_checkbox: yup.boolean(),
  });

export const integrationRulesWebWidget = {
  domains: { required: false, maxLength: LENGTH_XL },
  welcomeTriggerMessage: { required: false, maxLength: LENGTH_S },
  welcomeMessage: { required: false, maxLength: LENGTH_S },
  widgetPosition: { required: false, maxLength: LENGTH_XXS },
  backgroundColor: { required: false, maxLength: LENGTH_XXS },
  accentColor: { required: false, maxLength: LENGTH_XXS },
  headerTitle: { required: false, maxLength: LENGTH_S },
  greeting: { maxLength: LENGTH_S },
  visitor_information: webVisitorInformation,
};

export const suncoIntegration = {
  name: { required: false, maxLength: LENGTH_XS - 4 },
  welcome_trigger_message: { required: false, maxLength: LENGTH_S },
};

const zendeskParameter = yup
  .string()
  .required(() => t('validation.required'))
  .matches(slug)
  .max(LENGTH_L);

export const zendeskOAuthSchema = yup
  .object()
  .shape({
    company: yup.string().max(LENGTH_L),
    client_id: zendeskParameter,
    client_secret: zendeskParameter,
    subdomain: zendeskParameter,
  });

export const integrationSessionTimeoutSchema = yup.object().shape({
  session_timeout: yup
    .number()
    .nullable()
    .transform((value, originalValue) => {
      // If the field is empty, return null instead of NaN
      return originalValue === '' ? null : value;
    })
    .min(10, (v) => t('validation.minValue', { 0: v.min.toString() }))
    .max(20160, (v) => t('validation.maxValue', { 0: v.max.toString() })),
});

export const whatsappIntegrationConfig = {
  prefilled_message: { maxLength: LENGTH_M },
  list_button_text: { maxLength: 20 },
};

export const whatsappTestNumber = () => ({
  label: { required: true, maxLength: LENGTH_XS },
  phone_number: {
    required: t('validation.required'),
    validate: (value: string) => isValidPhoneNumber(value) || t('validation.invalid_phone_number'),
  },
  desk_id: { required: true },
});

export const frontIntegrationConfig = {
  teammate_id: { required: true },
  sender_name: { required: true },
};

export const customerRsaKey = { required: true, maxLength: LENGTH_XXXL / 2 };

export const welcomeTriggerMessageValidator = { maxLength: LENGTH_S };
